import { useEffect, useRef, useState, type ReactNode } from "react";

import type { LucideIcon } from "lucide-react";
import { Search } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * Command-palette style search modal: a borderless search input on top, a
 * scrollable result list below. The caller owns the query state and passes
 * already-filtered items — this component is pure presentation plus keyboard
 * handling (↑/↓ to move, Enter to select, Esc closes via Dialog).
 *
 * Each item carries the icon of the section it lives in, a title, an optional
 * snippet (use `highlightMatch` to emphasize the matching portion), and an
 * optional right-aligned hint (e.g. its lane or kind).
 */
export interface SearchModalItem {
  id: string;
  icon: LucideIcon;
  title: ReactNode;
  snippet?: ReactNode;
  hint?: string;
  onSelect: () => void;
}

export interface SearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  onQueryChange: (query: string) => void;
  items: SearchModalItem[];
  placeholder?: string;
  /** Shown when the query is non-empty and nothing matches. */
  emptyText?: string;
  /** Shown while the query is empty. */
  idleText?: string;
}

export function SearchModal({
  open,
  onOpenChange,
  query,
  onQueryChange,
  items,
  placeholder = "Search…",
  emptyText = "No matches.",
  idleText = "Start typing to search.",
}: SearchModalProps) {
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset selection whenever the result set changes.
  useEffect(() => setActive(0), [query, open]);

  const move = (delta: number) => {
    if (!items.length) return;
    const next = (active + delta + items.length) % items.length;
    setActive(next);
    listRef.current
      ?.querySelector(`[data-index="${next}"]`)
      ?.scrollIntoView({ block: "nearest" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <div className="flex items-center gap-2 border-b px-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            // eslint-disable-next-line jsx-a11y/no-autofocus -- a search palette exists to be typed into
            autoFocus
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                move(1);
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                move(-1);
              } else if (e.key === "Enter" && items[active]) {
                e.preventDefault();
                items[active].onSelect();
              }
            }}
            placeholder={placeholder}
            className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div ref={listRef} className="max-h-[28rem] overflow-y-auto p-1.5">
          {items.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {query.trim() ? emptyText : idleText}
            </p>
          ) : (
            items.map((item, i) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-index={i}
                  onClick={item.onSelect}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    "flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left",
                    i === active ? "bg-brand/10" : "hover:bg-muted",
                  )}
                >
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted">
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{item.title}</span>
                    {item.snippet ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {item.snippet}
                      </span>
                    ) : null}
                  </span>
                  {item.hint ? (
                    <span className="mt-0.5 shrink-0 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      {item.hint}
                    </span>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Wrap the matching portion of `text` so the hit is visible in results.
 *  Case-insensitive first match; returns the plain text when there's no hit. */
export function highlightMatch(text: string, query: string): ReactNode {
  const q = query.trim();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded-sm bg-brand/20 text-inherit">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

/** Clip `text` to a window around the first match so the hit is on screen
 *  even in a long description. */
export function matchSnippet(text: string, query: string, radius = 45): string {
  const q = query.trim();
  const idx = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (idx === -1) return text.length > radius * 2 ? `${text.slice(0, radius * 2)}…` : text;
  const start = Math.max(0, idx - radius);
  const end = Math.min(text.length, idx + q.length + radius);
  return `${start > 0 ? "…" : ""}${text.slice(start, end)}${end < text.length ? "…" : ""}`;
}
